/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { sortBy } from 'lodash'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { SubjectExtended } from 'hooks/usePlatformsAndSubjects'
import Table from 'components/Table'
import { Tooltip } from 'components/Tooltip'
import TableButtons from 'components/TableButtons/TableButtons'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { PageLoader } from 'components/PageLoader'
import { ClassroomTabLiveSwitcher } from 'components/ClassroomTabLiveSwitcher'
import { toast } from 'utils/toast'
import { isReadingSubject } from 'pagesNew/QuestionBanks/utils'
import { READING_QB, READING_TYPE_PARAM } from 'pagesNew/PracticeTests/consts'
import { getQBClassroomTab } from '../utils'
import * as classes from './styles'
import { CreateCategoryModal, EditCategoryModal } from './CategoryModals'
import { QuestionBank, QuestionBankCategory, useCategoriesApi } from './useCategoriesApi'

export type ListWithCategoriesProps = {
  selectedSubject: SubjectExtended
}

export const ListWithCategories = ({ selectedSubject }: ListWithCategoriesProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const categoryId = searchParams.get('categoryId')
  const { navigate, getLink } = useLink()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [openCategory, setOpenCategory] = useState<QuestionBankCategory | undefined>()
  const [selectedCategory, setSelectedCategory] = useState<QuestionBankCategory | undefined>()
  const [categories, setCategories] = useState<QuestionBankCategory[]>([])
  const [questionBanks, setQuestionBanks] = useState<QuestionBank[]>([])
  const [submittingQuestionBankId, setSubmittingQuestionBankId] = useState<string | undefined>()
  const [isLoaded, setIsLoaded] = useState(false)
  const categoriesApi = useCategoriesApi()

  const loadCategories = useCallback(async () => {
    if (!selectedSubject?.id) {
      return
    }

    try {
      const categories = await categoriesApi.getCategories(selectedSubject)
      setCategories(categories)
      setIsLoaded(true)
    } catch (e) {
      toast.error('Failed to load Question Bank Categories')
    }
  }, [selectedSubject?.tabId])

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  useEffect(() => {
    if (!categories.length) {
      setSelectedCategory(undefined)
      return
    }

    setSelectedCategory(
      (prevSelectedCategory) =>
        (prevSelectedCategory && categories.find((category) => category.id === prevSelectedCategory?.id)) ||
        (categoryId && categories.find((category) => category.id === categoryId)) ||
        categories[0]
    )
  }, [categories])

  useEffect(() => {
    if (!selectedCategory) {
      setQuestionBanks([])
      return
    }

    setQuestionBanks(sortBy(selectedCategory.questionBanks, 'order'))
    setSearchParams((prev) => ({ ...Object.fromEntries(prev.entries()), categoryId: selectedCategory?.id }))
  }, [selectedCategory])

  const handleAddCategoryClick = useCallback(() => {
    if (!selectedSubject) {
      return
    }

    setIsCreateModalOpen(true)
  }, [selectedSubject])

  const handleSortCategories = useCallback(
    async (categoriesUpd: QuestionBankCategory[]) => {
      if (!selectedSubject) {
        return
      }

      setCategories(categoriesUpd.map((category, order) => ({ ...category, order })))

      try {
        await categoriesApi.updateCategoriesOrder(selectedSubject, categoriesUpd)
        toast.success('Question Bank Categories order updated successfully')
      } catch (e) {
        toast.error('Question Bank Categories order update failed')
        await loadCategories()
      }
    },
    [selectedSubject, loadCategories]
  )

  const handleIsLiveCategoryChange = useCallback(
    async (category: QuestionBankCategory, isLiveUpd: boolean) => {
      if (!selectedSubject) {
        return
      }

      setCategories((prev) =>
        prev.map((currentCategory) => {
          if (category.id !== currentCategory.id) {
            return currentCategory
          }

          return {
            ...currentCategory,
            isLive: isLiveUpd,
          }
        })
      )

      try {
        await categoriesApi.updateCategoryIsLive(selectedSubject, category.id, isLiveUpd)
        toast.success('Question Bank Category updated successfully')
      } catch (e) {
        toast.error('Question Bank Category update failed')
        await loadCategories()
      }
    },
    [selectedSubject, loadCategories]
  )

  const handleAddQuestionBankClick = useCallback(() => {
    if (!selectedCategory || !selectedSubject) {
      return
    }

    if (isReadingSubject(selectedSubject.slug)) {
      navigate(cmsRoutes.PRACTICE_TEST_CREATE, undefined, {
        searchParams: new URLSearchParams({
          [READING_TYPE_PARAM]: READING_QB,
          subjectId: selectedSubject.id,
          categoryId: selectedCategory.id,
          defaultOrder: String(questionBanks.length),
        }),
      })
      return
    }

    navigate(cmsRoutes.QUESTION_BANK_CREATE, undefined, {
      searchParams: new URLSearchParams({
        categoryId: selectedCategory.id,
        defaultOrder: String(questionBanks.length),
        tabIndex: String(selectedSubject.tabIndex),
      }),
    })
  }, [selectedSubject, selectedCategory?.id, questionBanks.length])

  const handleSortQuestionBanks = useCallback(
    async (questionBanksUpd: QuestionBank[]) => {
      if (!selectedSubject) {
        return
      }

      const updatedQuestionBanksOrder = questionBanksUpd.map((questionBank, order) => ({ ...questionBank, order }))

      setQuestionBanks(updatedQuestionBanksOrder)

      setCategories((categories) =>
        categories.map((category) =>
          category.id === selectedCategory?.id
            ? {
                ...category,
                questionBanks: updatedQuestionBanksOrder,
              }
            : category
        )
      )

      try {
        await categoriesApi.updateQBsOrder(selectedSubject, questionBanksUpd)
        toast.success('Question Banks order updated successfully')
      } catch (e) {
        toast.error('Question Banks order update failed')
        await loadCategories()
      }
    },
    [selectedCategory, selectedSubject, loadCategories]
  )

  const handleIsLiveQuestionBankChange = useCallback(
    async (category: QuestionBankCategory | undefined, questionBank: QuestionBank, isLiveUpd: boolean) => {
      if (!selectedSubject || !category) {
        return
      }

      setSubmittingQuestionBankId(questionBank.id)

      let isLiveQuestionBanksExists = false
      setCategories((prev) =>
        prev.map((currentCategory) => {
          if (category.id !== currentCategory.id) {
            return currentCategory
          }

          const questionBanks = currentCategory.questionBanks.map((currentQuestionBank) => {
            if (questionBank.id !== currentQuestionBank.id) {
              isLiveQuestionBanksExists = isLiveQuestionBanksExists || currentQuestionBank.isLive
              return currentQuestionBank
            }

            isLiveQuestionBanksExists = isLiveQuestionBanksExists || isLiveUpd
            return {
              ...currentQuestionBank,
              isLive: isLiveUpd,
            }
          })

          return {
            ...currentCategory,
            questionBanks,
            isLive: !isLiveQuestionBanksExists ? false : currentCategory.isLive,
          }
        })
      )

      try {
        await categoriesApi.updateQBIsLive(selectedSubject, questionBank.id, isLiveUpd)

        if (!isLiveQuestionBanksExists) {
          await categoriesApi.updateCategoryIsLive(selectedSubject, category.id, false)
        }
        toast.success('Question Bank updated successfully')
      } catch (e) {
        toast.error('Question Bank update failed')
        await loadCategories()
      }

      setSubmittingQuestionBankId(undefined)
    },
    [selectedSubject, loadCategories]
  )

  const getEditLink = useCallback(
    (questionBankId: string) => {
      if (!selectedSubject) {
        return ''
      }

      const searchParams = new URLSearchParams({
        tabIndex: String(selectedSubject.tabIndex),
      })

      if (isReadingSubject(selectedSubject.slug)) {
        return getLink(
          cmsRoutes.PRACTICE_TEST_EDIT,
          {
            practiceTestId: questionBankId,
          },
          searchParams
        )
      }

      return getLink(
        cmsRoutes.QUESTION_BANK_EDIT,
        {
          questionBankId,
        },
        searchParams
      )
    },
    [selectedSubject]
  )

  if (!isLoaded) {
    return <PageLoader />
  }

  const classroomTabEnum = getQBClassroomTab(selectedSubject)

  return (
    <div css={classes.tablesWrapper}>
      <div css={classes.categoriesWrapper}>
        <div css={classes.tableHeader}>
          <Typography variant="body1" css={classes.tableTitle}>
            Question Bank Categories
          </Typography>
          <ClassroomTabLiveSwitcher selectedSubject={selectedSubject} tabEnum={classroomTabEnum} />
        </div>
        <Table<QuestionBankCategory>
          css={classes.categoriesTable}
          columns={[
            {
              title: 'Title',
              renderCell: (category) => category.title,
            },
            {
              title: 'Live',
              renderCell: (category) => {
                const isLiveQuestionBanksExists = category.questionBanks.some(({ isLive }) => isLive)

                if (isLiveQuestionBanksExists) {
                  return (
                    <Switch
                      checked={category.isLive}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, isLive) => handleIsLiveCategoryChange(category, isLive)}
                      disabled={category.id === selectedCategory?.id && !!submittingQuestionBankId}
                    />
                  )
                }

                return (
                  <Tooltip
                    title="You cannot enable Live for Category with an empty list of Question Banks with Live enabled"
                    isNote
                    arrow
                  >
                    <Switch
                      checked={category.isLive}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, isLive) => handleIsLiveCategoryChange(category, isLive)}
                      disabled
                    />
                  </Tooltip>
                )
              },
              flex: '0 1 50px',
            },
            {
              renderCell: (category) => (
                <IconButton
                  size="small"
                  css={classes.link}
                  onClick={() => setOpenCategory(category)}
                  disabled={category.id === selectedCategory?.id && !!submittingQuestionBankId}
                >
                  <EditIcon />
                </IconButton>
              ),
              flex: '0 1 30px',
            },
          ]}
          items={categories}
          onSort={handleSortCategories}
          activeItem={selectedCategory}
          onItemClick={setSelectedCategory}
          isSortable
        />
        <TableButtons
          itemName="Question Bank Category"
          onAddItem={handleAddCategoryClick}
          disabled={!selectedSubject}
        />
      </div>
      <div css={classes.questionBanksWrapper}>
        <Typography variant="body1" css={[classes.tableTitle, classes.tableHeader]}>
          Question Banks
        </Typography>
        <Table<QuestionBank>
          css={classes.questionBanksTable}
          columns={[
            {
              title: 'Title',
              renderCell: (questionBank) => questionBank.title,
            },
            {
              title: (
                <div css={classes.countWrapper}>
                  <div css={classes.countTitle}># of Questions</div>
                </div>
              ),
              renderCell: (questionBank) => <div css={classes.countValue}>{questionBank.questionIds.length}</div>,
              flex: '0 1 50px',
            },
            {
              title: 'Live',
              renderCell: (questionBank) => (
                <Switch
                  checked={questionBank.isLive}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e, isLive) => handleIsLiveQuestionBankChange(selectedCategory, questionBank, isLive)}
                  disabled={questionBank.id === submittingQuestionBankId}
                />
              ),
              flex: '0 1 50px',
            },
            {
              renderCell: (questionBank) => (
                <Link to={getEditLink(questionBank.id)}>
                  <IconButton size="small" css={classes.link}>
                    <EditIcon />
                  </IconButton>
                </Link>
              ),
              flex: '0 1 30px',
            },
          ]}
          items={questionBanks}
          onSort={handleSortQuestionBanks}
          isSortable
        />
        <TableButtons itemName="Question Bank" onAddItem={handleAddQuestionBankClick} disabled={!selectedCategory} />
      </div>
      {!!selectedSubject && (
        <>
          <CreateCategoryModal
            open={isCreateModalOpen}
            subject={selectedSubject}
            defaultOrder={categories.length}
            onClose={() => setIsCreateModalOpen(false)}
            onRefetch={loadCategories}
          />
          <EditCategoryModal
            open={!!openCategory}
            subject={selectedSubject}
            questionBankCategory={openCategory as QuestionBankCategory}
            onClose={() => setOpenCategory(undefined)}
            onRefetch={loadCategories}
          />
        </>
      )}
    </div>
  )
}
